import { useState } from "react";
import { MapPin, Calendar, Clock, Euro, Users, CheckCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GOOGLE_SHEETS_WEBHOOK = "https://script.google.com/macros/s/AKfycbxAZitlyDSYgsc0ytuY2QVQBNPTLpCez08FfeaauhLDpkJopJOuMzGey11dyuAdU0Kc/exec";

export default function LandingPage() {
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    chiesa: "",
    apericena: "",
    is_pastore: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nome || !formData.cognome || !formData.email || !formData.chiesa || !formData.is_pastore || !formData.apericena) {
      setError("Compila tutti i campi per favore.");
      return;
    }

    setLoading(true);
    try {
      // Fire and forget - Google Apps Script doesn't return readable response with no-cors
      fetch(GOOGLE_SHEETS_WEBHOOK, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(formData),
      });
      // Show confirmation immediately
      setTimeout(() => {
        setSubmitted(true);
        setLoading(false);
      }, 800);
    } catch (err) {
      setError("Errore durante l'iscrizione. Riprova.");
      setLoading(false);
    }
  };

  return (
    <div
      className="max-w-md mx-auto min-h-screen relative overflow-hidden"
      style={{ backgroundColor: "#DDA7A5", fontFamily: "'Work Sans', sans-serif" }}
      data-testid="landing-page"
    >
      {/* Noise overlay */}
      <div className="noise-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/grain-bg.jpg)` }} />

      {/* Brush stroke blobs */}
      <div className="brush-stroke brush-1" />
      <div className="brush-stroke brush-2" />
      <div className="brush-stroke brush-3" />
      <div className="brush-stroke brush-4" />

      {/* Content wrapper */}
      <div className="relative z-10 px-5 py-8 flex flex-col gap-8">

        {/* Top bar */}
        <div className="flex items-center justify-between animate-enter animate-delay-1">
          <div style={{ fontFamily: "'Syne', sans-serif" }} className="font-extrabold text-sm tracking-tight text-[#1B2FA0] uppercase">
            SuperLife | ICF Reggio Emilia
          </div>
          <div className="accent-tag">
            16 MAG
          </div>
        </div>

        {/* Hero title image */}
        <div className="animate-enter animate-delay-2">
          <img
            src="/title.webp"
            alt="Voglio una vita Super - Giovanni 10:10"
            className="w-full h-auto block"
            data-testid="hero-title-image"
          />
        </div>

        {/* Guest section */}
        <div className="animate-enter animate-delay-3">
          <div className="neo-card flex flex-col items-center text-center gap-3">
            <div className="accent-tag" style={{ transform: "rotate(-2deg)" }}>Special Guest</div>
            <div className="relative">
              <div
                className="w-44 h-44 rounded-full overflow-hidden border-[3px] border-[#1B2FA0] mx-auto"
                style={{ boxShadow: "4px 4px 0px 0px #1B2FA0" }}
              >
                <img
                  src="/stefy.webp"
                  alt="Stefania Spezzacatena"
                  className="w-full h-full object-cover"
                  data-testid="guest-photo"
                />
              </div>
            </div>
            <div>
              <h3
                style={{ fontFamily: "'Syne', sans-serif" }}
                className="text-2xl font-extrabold text-[#1B2FA0] uppercase tracking-tight leading-tight"
              >
                Stefania<br />Spezzacatena
              </h3>
              <p className="text-sm font-semibold text-[#444] mt-1 uppercase tracking-wide">
                Past. YOUS Milano
              </p>
            </div>
          </div>
        </div>

        {/* Event details */}
        <div className="animate-enter animate-delay-4">
          <div className="neo-card">
            <h2
              style={{ fontFamily: "'Syne', sans-serif" }}
              className="text-xl font-extrabold text-[#1B2FA0] uppercase tracking-tight mb-4"
            >
              Dettagli Evento
            </h2>

            <div className="flex flex-col gap-3">
              <div className="detail-row">
                <Calendar className="detail-icon text-[#1B2FA0]" strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-[#1B2FA0]">16 Maggio 2026</div>
                </div>
              </div>

              <div className="detail-row">
                <Clock className="detail-icon text-[#1B2FA0]" strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-[#1B2FA0]">Ore 18:00</div>
                </div>
              </div>

              <div className="detail-row">
                <MapPin className="detail-icon text-[#1B2FA0]" strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-[#1B2FA0]">Via Cocchi 4/A</div>
                  <div className="text-sm text-[#444]">Reggio Emilia</div>
                </div>
              </div>

              <div className="detail-row">
                <Euro className="detail-icon text-[#1B2FA0]" strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-[#1B2FA0]">Evento Gratuito</div>
                  <div className="text-sm text-[#444]">Apericena: 6,00 EUR<br />(in contanti)</div>
                </div>
              </div>

              <div className="detail-row">
                <Users className="detail-icon text-[#1B2FA0]" strokeWidth={2.5} />
                <div>
                  <div className="font-bold text-[#1B2FA0]">Iscrizioni entro il 10 Maggio 2026</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Registration form */}
        <div className="animate-enter animate-delay-5">
          {!submitted ? (
            <div className="neo-card">
              <h2
                style={{ fontFamily: "'Syne', sans-serif" }}
                className="text-xl font-extrabold text-[#1B2FA0] uppercase tracking-tight mb-5"
              >
                Iscriviti
              </h2>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="registration-form">
                <div>
                  <label className="form-label" htmlFor="nome">Nome</label>
                  <input
                    id="nome"
                    name="nome"
                    type="text"
                    className="neo-input"
                    placeholder="Il tuo nome"
                    value={formData.nome}
                    onChange={handleChange}
                    data-testid="input-nome"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="cognome">Cognome</label>
                  <input
                    id="cognome"
                    name="cognome"
                    type="text"
                    className="neo-input"
                    placeholder="Il tuo cognome"
                    value={formData.cognome}
                    onChange={handleChange}
                    data-testid="input-cognome"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="neo-input"
                    placeholder="La tua email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="chiesa">Di quale chiesa fai parte?</label>
                  <input
                    id="chiesa"
                    name="chiesa"
                    type="text"
                    className="neo-input"
                    placeholder="Nome della tua chiesa"
                    value={formData.chiesa}
                    onChange={handleChange}
                    data-testid="input-chiesa"
                  />
                </div>

                <div>
                  <label className="form-label">Sei un pastore/responsabile giovani?</label>
                  <Select
                    onValueChange={(val) => setFormData({ ...formData, is_pastore: val })}
                    value={formData.is_pastore}
                  >
                    <SelectTrigger className="neo-select-trigger" data-testid="select-pastore">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si" data-testid="select-option-si">Si</SelectItem>
                      <SelectItem value="no" data-testid="select-option-no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="form-label">Partecipi all'apericena?</label>
                  <Select
                    onValueChange={(val) => setFormData({ ...formData, apericena: val })}
                    value={formData.apericena}
                  >
                    <SelectTrigger className="neo-select-trigger" data-testid="select-apericena">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si" data-testid="select-apericena-si">Si</SelectItem>
                      <SelectItem value="no" data-testid="select-apericena-no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {error && (
                  <div className="text-red-700 text-sm font-semibold bg-red-100 border-2 border-red-700 p-3" data-testid="form-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="neo-submit mt-2"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? "Invio in corso..." : "Iscriviti ora"}
                </button>
              </form>
            </div>
          ) : (
            <div className="confirmation-card" data-testid="confirmation-message">
              <CheckCircle className="mx-auto mb-3" size={48} strokeWidth={2.5} color="#1B2FA0" />
              <h2
                style={{ fontFamily: "'Syne', sans-serif" }}
                className="text-2xl font-extrabold text-[#1B2FA0] uppercase tracking-tight mb-2"
              >
                Iscrizione Confermata!
              </h2>
              <p className="text-[#1B2FA0] font-medium">
                Grazie {formData.nome}! Ti aspettiamo il 16 Maggio 2026.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-[#1B2FA0]/60 pb-4" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          SuperLife | ICF Reggio Emilia
        </div>
      </div>
    </div>
  );
}
